import { useEffect, useState } from 'react'

import { isAvatarImageLoaded, preloadAvatarImage } from './utils/preloadAvatarImage'

/**
 * Tracks image loading state for avatar sources.
 * Returns the `src` only after the image has fully loaded, retaining the
 * previously loaded image while a new one is in flight so the avatar
 * never flickers or shows a blank placeholder.
 */
export function useAvatarImage(src: string | undefined): string | undefined {
  const [loadedSrc, setLoadedSrc] = useState<string | undefined>(() =>
    src && isAvatarImageLoaded(src) ? src : undefined
  )

  useEffect(() => {
    if (!src) {
      return
    }

    if (loadedSrc === src) {
      return
    }

    if (isAvatarImageLoaded(src)) {
      setLoadedSrc(src)
      return
    }

    let cancelled = false

    void preloadAvatarImage(src)
      .then(() => {
        if (!cancelled) {
          setLoadedSrc(src)
        }
      })
      .catch(() => {})

    return () => {
      cancelled = true
    }
  }, [src, loadedSrc])

  return loadedSrc
}
